import { useMemo } from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  DocumentAnalysis,
  PaymentFrequency,
  createDocumentAnalysisDocumentUrl,
} from '@admiin-com/ds-graphql';
import { S3Upload, convertCentsToAmount } from '@admiin-com/ds-common';
import {
  BillCreateFormData,
  BillCreateFormDataDefaultValues,
} from './TaskCreationProvider';

export const useDocumentAnalysisToFormValues = (
  documentAnalysis: DocumentAnalysis | null
) => {
  const [createDocumentUrl] = useMutation(
    gql(createDocumentAnalysisDocumentUrl)
  );

  const convertToFormValues = async (): Promise<
    Partial<BillCreateFormData>
  > => {
    if (!documentAnalysis) {
      return {};
    }

    let url: string | undefined;
    try {
      const res = await createDocumentUrl({
        variables: { input: { documentAnalysisId: documentAnalysis.id } },
      });
      url = res.data?.createDocumentAnalysisDocumentUrl?.url;
    } catch (e) {
      console.error('Error creating document URL:', e);
    }

    const documents: S3Upload[] =
      documentAnalysis.documents?.map((doc) => ({
        key: doc?.key ?? '',
        identityId: doc?.identityId ?? '',
        level: doc?.level as any,
        type: doc?.type as any,
        name: doc?.key ?? '',
        size: '',
        src: url,
        source: documentAnalysis.source === 'EMAIL' ? 'EMAIL' : 'UPLOAD',
      })) ?? [];

    return {
      ...BillCreateFormDataDefaultValues,
      documents,
      documentAnalysisId: documentAnalysis.id,
      reference: documentAnalysis.task?.reference ?? '',
      bpayReferenceNumber: documentAnalysis.bpay?.referenceNumber ?? '',
      dueAt: documentAnalysis.task?.dueAt ?? '',
      documentType: documentAnalysis.task?.documentType ?? undefined,
      settlementStatus: documentAnalysis.task?.settlementStatus ?? undefined,
      period: documentAnalysis.task?.period ?? undefined,
      year: documentAnalysis.task?.year ?? undefined,
      futureTaskInput: {
        from: '',
        to: '',
        bpayReferenceNumber: documentAnalysis.bpay?.referenceNumber ?? '',
        amount: convertCentsToAmount(documentAnalysis.task?.amount ?? 0),
        dueAt: documentAnalysis.task?.dueAt ?? '',
        reference: documentAnalysis.task?.reference ?? '',
        issuedAt: documentAnalysis.task?.issuedAt ?? '',
        paymentFrequency:
          documentAnalysis.task?.paymentFrequency ?? PaymentFrequency.ONCE,
        numberOfPayments: documentAnalysis.task?.numberOfPayments ?? 1,
        lineItems:
          documentAnalysis.task?.lineItems?.map((lineItem) => ({
            description: lineItem?.description ?? '',
            quantity: lineItem?.quantity ?? 0,
            unitPrice: convertCentsToAmount(lineItem?.unitPrice ?? 0),
            price: convertCentsToAmount(lineItem?.price ?? 0),
            taxType: lineItem?.taxType ?? undefined,
          })) ?? [],
      },
    };
  };

  return { convertToFormValues };
};
